import React, { useState } from 'react';
import PanelErrorBoundary from '../../shared/PanelErrorBoundary';
import { useTileData } from '../../../hooks/useTileData';
import { getClientRequests, getClientsWithoutOwner, getMyClients, suggestRecipient } from '../../../api/hub';
import ElsewhereLinks from '../ElsewhereLinks';
import { useToolSelection } from './toolSelection';
import ClientDetailLayout from './ClientDetailLayout';
import ClientDetailSkeleton from './ClientDetailSkeleton';
import ClientHeader from './ClientHeader';
import AttentionStrip from './AttentionStrip';
import WorkCard from './WorkCard';
import RequestsCard, { statusLine } from './RequestsCard';
import DrillNumber from './DrillNumber';
import ClientSearch from './ClientSearch';
import ClientActions from './ClientActions';
import BackLink from './BackLink';
import useClientSearch from './useClientSearch';
import { drillTargetFor } from './drill';
import { DrillSheet } from '../kit';
import { countChips } from './WorkCard';
import ThreadsCard from './ThreadsCard';
import RequestsSheet from './RequestsSheet';
import OpenWorkSheet from './OpenWorkSheet';
import GatedRow from './GatedRow';
import ActivityCard from './ActivityCard';
import PeopleCard from './PeopleCard';
import PinnedNotes from './PinnedNotes';
import { mergeClientFacts, withWebsite } from './clientFacts';
import { suggestedContact } from './peopleModel';
import { isPending } from './states';

// Static blue mono text for a header link whose destination does not exist yet.
const ACTION_LINK = 'text-sm text-brand';

// The client detail layout: three zones in
// a fixed order. HubClientDetailPage owns access gating and the three page-level
// reads (context, evidence, notes) and hands their useTileData results in.

/**
 * The account owner the header can truthfully show: a name when the backend
 * supplies one, "unassigned" when v_clients_without_an_owner lists the client,
 * and null (field omitted) when neither is known — never a guess.
 * @param {object|null} client        fn_client_context.client
 * @param {object[]|undefined} unownedRows  v_clients_without_an_owner rows for this client
 * @returns {{name?:string, unassigned?:boolean}|null}
 */
export const ownerOf = (client, unownedRows) => {
  if (client?.owner?.name) return { name: client.owner.name };
  if (Array.isArray(unownedRows) && unownedRows.length > 0) return { unassigned: true };
  return null;
};

/**
 * @param {object} props
 * @param {string} props.id            Client id from the route.
 * @param {object} props.context       useTileData result for fn_client_context.
 * @param {object} props.evidence      useTileData result for fn_client_evidence.
 * @param {object} props.notes         useTileData result for ops_notes.
 */
export default function ClientDetailView({ id, context, evidence, notes }) {
  // Bumped by the header's Compose button; the Activity card scrolls to and focuses the composer.
  const [composeSignal, setComposeSignal] = useState(0);
  // One in-page search over what is loaded here; and the open drill, if any.
  const search = useClientSearch(id);
  const [drill, setDrill] = useState(null);
  // The slide-over the two header links open when no drill is confirmed: 'open' | 'requests' | null.
  const [sheet, setSheet] = useState(null);
  const requests = useTileData(() => getClientRequests(id), [id], !!id);
  const unowned = useTileData(() => getClientsWithoutOwner(id), [id], !!id);
  // Read once here: the People card shows it and the header borrows its top person as a
  // stand-in contact, so one read feeds both.
  const recipients = useTileData(() => suggestRecipient(id), [id], !!id);
  const selectForTool = useToolSelection(id);

  const ctx = context.data?.data || null;
  const ev = evidence.data?.data || null;

  const ctxPending = isPending(context);

  // The roster is only read when the header would otherwise have no name — i.e. the
  // context read failed and no source named the client. That becomes true AFTER mount,
  // so the flag is a dependency: the hook re-reads when it flips on.
  const sourceName = ev?.name || ctx?.client?.name || null;
  const needRoster = !sourceName && !!context.error;
  const roster = useTileData(getMyClients, [needRoster], needRoster);
  const rosterName = (roster.data?.data || []).find((c) => c?.id === id)?.name || null;

  // The stand-in contact only exists once the recipient read has settled successfully: while
  // it loads (a switch to another client keeps the previous rows until then) or after it
  // fails, the header keeps its normal placeholders.
  const recipientsSettled = !recipients.loading && !isPending(recipients) && !recipients.error;
  const fallbackContact = recipientsSettled ? suggestedContact(recipients.data?.data) : null;

  const facts = mergeClientFacts({ evidence: ev, client: ctx?.client, rosterName, fallbackContact });

  // Everything still in flight and nothing to show yet → the page-shaped skeleton.
  if (ctxPending && isPending(evidence)) return <ClientDetailSkeleton />;

  // Blocks fed by the context read are loading until it has answered.
  const contextLoading = context.loading || ctxPending;

  const pinnedNotes = (notes.data?.data || []).filter((n) => n && n.is_pinned === true);
  const retryContext = () => context.refresh?.();

  // The search narrows the sampled work ITEMS and the requests; the counts stay the truth.
  const allItems = Array.isArray(ctx?.work?.items) ? ctx.work.items : [];
  const items = search.active
    ? allItems.filter((it) => search.matches(it?.title, it?.owner, it?.work_type, it?.status, it?.stalled_reason))
    : allItems;
  const work = ctx?.work ? { ...ctx.work, items } : ctx?.work;
  const itemsEmptyMessage = search.active && allItems.length > 0 && items.length === 0
    ? `No work items match “${search.label}”.` : null;

  const allRequests = requests.data?.data;
  const requestRows = search.active
    ? (allRequests || []).filter((r) => search.matches(r?.title, r?.requested_by, r?.status, statusLine(r)))
    : allRequests;
  const requestsEmptyMessage = search.active && (allRequests || []).length > 0
    ? `No requests match “${search.label}”.` : undefined;

  // A drill exists only where one is confirmed (see drill.js); the count chips stay plain text until
  // then. The two header links do not wait for one: each opens its own slide-over (this client's
  // open work, or every request already loaded), and a confirmed drill, once added, takes over.
  const drillFor = (bucket) => drillTargetFor(bucket, id);
  const sheetLink = (bucket, which, label, text) => (drillFor(bucket) ? (
    <DrillNumber drill={drillFor(bucket)} label={label} onOpen={setDrill} className={ACTION_LINK}>{text}</DrillNumber>
  ) : (
    <button
      type="button" aria-label={label} onClick={() => setSheet(which)}
      className={`${ACTION_LINK} rounded underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand`}
    >
      {text}
    </button>
  ));
  const openCount = countChips(ctx?.work).find((c) => c.key === 'open')?.count;
  const openAction = openCount ? sheetLink('open', 'open', `See the ${openCount} open items`, `${openCount} open →`) : null;
  // Decided from the UNFILTERED list, and only once the read has settled: a search that
  // filters every row out must not hide the link (it is a total, not a filtered count),
  // and neither a loading nor a failed read says there are no requests.
  const requestsSettled = !requests.loading && !isPending(requests) && !requests.error;
  const requestsAction = requestsSettled && Array.isArray(allRequests) && allRequests.length > 0 ? (
    sheetLink('requests', 'requests', 'See all requests', 'All requests →')
  ) : null;

  return (
    <>
      <ClientDetailLayout
        header={
          <ClientHeader
            facts={facts}
            owner={ownerOf(ctx?.client, unowned.data?.data)}
            loading={!facts.name && (ctxPending || isPending(evidence))}
            contextError={context.error}
            onRetry={retryContext}
            actions={<ClientActions onCompose={() => setComposeSignal((n) => n + 1)} />}
            back={<BackLink />}
          />
        }
        strip={<AttentionStrip clientId={id} />}
        pinned={<PinnedNotes notes={pinnedNotes} />}
        main={
          <>
            <WorkCard
              work={work} loading={contextLoading} error={context.error} onRetry={retryContext}
              action={openAction} itemsEmptyMessage={itemsEmptyMessage} drillFor={drillFor} onDrill={setDrill}
            />
            <RequestsCard
              clientId={id}
              requests={requestRows}
              loading={requests.loading || isPending(requests)}
              error={requests.error}
              onRetry={() => requests.refresh?.()}
              action={requestsAction}
              {...(requestsEmptyMessage ? { emptyMessage: requestsEmptyMessage } : {})}
            />
            <ThreadsCard threads={ctx?.threads} loading={contextLoading} error={context.error} onRetry={retryContext} />
            {/* The two gated rows close the column, after Conversations. */}
            <div className="space-y-3">
              <GatedRow label="Billing" component="billing panel" loading={contextLoading} error={context.error} value={ctx?.billing} />
              <GatedRow label="Effort" component="effort panel" loading={contextLoading} error={context.error} value={ctx?.effort} />
            </div>
          </>
        }
        rail={<PeopleCard clientId={id} recipients={recipients} onOwnerAssigned={() => unowned.refresh?.()} />}
        activity={
          <ActivityCard
            clientId={id} focusSignal={composeSignal} query={search.query}
            search={<ClientSearch value={search.query} onChange={search.setQuery} onClear={search.clear} />}
          />
        }
        footer={
          <PanelErrorBoundary label="Elsewhere">
            {contextLoading ? null : <ElsewhereLinks elsewhere={withWebsite(ctx?.elsewhere, facts.website)} onOpen={selectForTool} />}
          </PanelErrorBoundary>
        }
      />
      <OpenWorkSheet open={sheet === 'open'} onClose={() => setSheet(null)} clientId={id} />
      <RequestsSheet open={sheet === 'requests'} onClose={() => setSheet(null)} requests={allRequests} />
      <DrillSheet open={!!drill} onClose={() => setDrill(null)} what={drill?.what} id={drill?.id} scope={drill?.scope} />
    </>
  );
}
